import { useState, type FC } from 'react';
import { Button, Checkbox } from '@selfinspectionorg/ui-kit';
import styles from './Mfa.module.scss';
import { Card } from '@/components/ui';

interface Props {
  codes: string[];
  onAcknowledge: () => void;
}

const DESCRIPTION =
  'Store these codes somewhere safe. Each can be used once if you lose access to your authenticator. ' +
  "They won't be shown again.";

/**
 * One-time backup-code gate for TOTP enrollment. The codes are shown once and cannot be retrieved
 * later, so the session is not adopted (which would redirect away) until the user explicitly
 * acknowledges having saved them.
 */
export const BackupCodes: FC<Props> = ({ codes, onAcknowledge }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const copy = () => {
    void navigator.clipboard?.writeText(codes.join('\n'));
  };

  const download = () => {
    const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'backup-codes.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Card.Title title="Save your backup codes" description={DESCRIPTION} />
      <div className={styles.body}>
        <div className={styles.codesGrid} data-testid="mfa-backup-codes">
          {codes.map((code) => (
            <span key={code} className={styles.codeItem}>
              {code}
            </span>
          ))}
        </div>
        <div className={styles.codeToolbar}>
          <Button.Ghost onClick={copy} data-testid="mfa-backup-copy-button">
            Copy
          </Button.Ghost>
          <Button.Ghost onClick={download} data-testid="mfa-backup-download-button">
            Download
          </Button.Ghost>
        </div>
        <label className={styles.acknowledge}>
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            data-testid="mfa-backup-ack-checkbox"
          />
          <span className={styles.acknowledgeLabel}>I have saved my backup codes</span>
        </label>
        <Button width="100%" onClick={onAcknowledge} disabled={!acknowledged} data-testid="mfa-backup-continue-button">
          Continue
        </Button>
      </div>
    </>
  );
};
